import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, Box, Flex, Heading, Text } from 'rebass'
import { ApprovalListItem, BannerStatus } from '../../../interfaces/banners'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from '../../../store/rootReducer'
import { AuthStateProps } from '../../../interfaces/auth'
import { DropDownObjectProps } from '../../../interfaces/interfaces'
import { parseUtcDate, utcToLocalDisplay } from '../../../utils/dateTime'
import { UIStateProps } from '../../../interfaces/ui'
import { displayModal } from '../../../store/actions/ui'

type TileCardProps = {
  id: string
  bannerStatus: BannerStatus | string
  bannerType: number
  bannerSeverity: number
  orgName: string
  startTime: string
  endTime: string
  message: string
  createdBy: string
  createdBy34?: string
  approvalList?: ApprovalListItem[]
  scope: string
  onVoteUp?: () => void
  onVoteDown?: () => void
  onDelete?: () => void
  onClose?: () => void
  onEdit?: () => void
}

const TileCard: React.FC<TileCardProps> = ({
  bannerStatus,
  bannerType,
  bannerSeverity,
  orgName,
  startTime,
  endTime,
  message,
  createdBy,
  createdBy34,
  approvalList,
  scope,
  onVoteUp,
  onVoteDown,
  onDelete,
  onClose,
  onEdit,
}) => {
  const dispatch = useDispatch()
  const { options, hca34 } = useSelector<AppState, Partial<AuthStateProps>>(
    ({ auth: { options, hca34 } }) => ({ options, hca34 })
  )
  const { triggerModal } = useSelector<AppState, Partial<UIStateProps>>(
    ({ ui: { triggerModal } }) => ({ triggerModal })
  )

  const [menuOpen, setMenuOpen] = useState(false)
  const [isDeletePending, setIsDeletePending] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const organizationNameById = useMemo(() => {
    const nameMap: Record<string, string> = {}
    const orgLists = (options || {}) as Record<
      string,
      DropDownObjectProps[] | undefined
    >

    Object.values(orgLists).forEach((list) => {
      list?.forEach((org) => {
        if (org?.id) {
          nameMap[org.id] = org.text || org.id
        }
      })
    })

    return nameMap
  }, [options])

  const scopeDisplay = useMemo(
    () =>
      scope
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((scopeId) => organizationNameById[scopeId] || scopeId)
        .join(' | '),
    [organizationNameById, scope]
  )

  useEffect(() => {
    if (
      isDeletePending &&
      triggerModal?.type === 'closeBanner' &&
      triggerModal?.confirmation === 'remove'
    ) {
      onClose?.()
      setIsDeletePending(false)
      dispatch(
        displayModal({ show: false, type: undefined, confirmation: undefined })
      )
      return
    }

    if (
      isDeletePending &&
      triggerModal?.type === 'deleteBanner' &&
      triggerModal?.confirmation === 'delete'
    ) {
      onDelete?.()
      setIsDeletePending(false)
      dispatch(
        displayModal({ show: false, type: undefined, confirmation: undefined })
      )
      return
    }

    if (isDeletePending && triggerModal?.confirmation === 'cancel') {
      setIsDeletePending(false)
      return
    }

    if (isDeletePending && !triggerModal?.show) {
      setIsDeletePending(false)
    }
  }, [dispatch, isDeletePending, onClose, onDelete, triggerModal])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const formatApprovalTimestamp = (timestamp: string) => {
    const date = parseUtcDate(timestamp)

    if (!date) return timestamp

    const month = date.toLocaleString('en-US', { month: 'long' })
    const day = date.getDate()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${month} ${day}, ${hours}:${minutes}`
  }

  // Determine user permissions and banner status
  const normalizedStatus = bannerStatus.trim().toLowerCase()
  const isUpcoming = normalizedStatus === BannerStatus.Upcoming.toLowerCase()
  const isActive = normalizedStatus === BannerStatus.Active.toLowerCase()
  const isPendingApproval =
    normalizedStatus === BannerStatus.PendingApproval.toLowerCase()

  const normalizedCurrentUser = (hca34 || '').trim().toLowerCase()
  const normalizedCreatorHca34 = (createdBy34 || '').trim().toLowerCase()
  const normalizedCreatorDisplay = (createdBy || '').trim().toLowerCase()
  const currentUserApproval = (approvalList || []).find((appr) => {
    const normalizedApproverHca34 = (appr.hca34 || '').trim().toLowerCase()
    const normalizedApproverUserName = (appr.userName || '')
      .trim()
      .toLowerCase()

    return (
      !!normalizedCurrentUser &&
      (normalizedCurrentUser === normalizedApproverHca34 ||
        normalizedCurrentUser === normalizedApproverUserName)
    )
  })
  const hasApprovedByMe = !!currentUserApproval?.isApproved
  const hasDisapprovedByMe =
    currentUserApproval !== undefined && !currentUserApproval.isApproved
  const isCreator =
    !!normalizedCurrentUser &&
    (normalizedCurrentUser === normalizedCreatorHca34 ||
      normalizedCurrentUser === normalizedCreatorDisplay)

  const shouldShowMenu = isUpcoming || isActive || isPendingApproval
  const showEditOption = (isUpcoming || isPendingApproval) && isCreator
  const showApproveDisapproveOptions = isPendingApproval
  const showDeleteOption = isUpcoming || isActive || isPendingApproval
  const showCloseOption = isUpcoming || isActive

  return (
    <Card
      sx={{
        position: 'relative',
        p: 4,
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        borderRadius: 12,
        bg: 'white',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* 3-dots menu button */}
      {shouldShowMenu && (
        <Box ref={menuRef} sx={{ position: 'absolute', top: 10, right: 10 }}>
          <Box
            sx={{ cursor: 'pointer', fontSize: 20 }}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            &#8942;
          </Box>

          {menuOpen && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                mt: 2,
                bg: 'white',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                borderRadius: 4,
                overflow: 'hidden',
                zIndex: 10,
              }}
            >
              {showEditOption && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': { bg: 'lightgray' },
                  }}
                  onClick={() => {
                    onEdit?.()
                    setMenuOpen(false)
                  }}
                >
                  Edit
                </Box>
              )}
              {showApproveDisapproveOptions && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    cursor: hasApprovedByMe ? 'not-allowed' : 'pointer',
                    color: hasApprovedByMe ? 'gray' : 'black',
                    '&:hover': { bg: hasApprovedByMe ? 'white' : 'lightgray' },
                  }}
                  onClick={() => {
                    if (hasApprovedByMe) return
                    onVoteUp?.()
                    setMenuOpen(false)
                  }}
                >
                  Approve
                </Box>
              )}
              {showApproveDisapproveOptions && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    cursor: hasDisapprovedByMe ? 'not-allowed' : 'pointer',
                    color: hasDisapprovedByMe ? 'gray' : 'black',
                    '&:hover': {
                      bg: hasDisapprovedByMe ? 'white' : 'lightgray',
                    },
                  }}
                  onClick={() => {
                    if (hasDisapprovedByMe) return
                    onVoteDown?.()
                    setMenuOpen(false)
                  }}
                >
                  Disapprove
                </Box>
              )}
              {showCloseOption && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': { bg: 'lightgray' },
                  }}
                  onClick={() => {
                    setMenuOpen(false)
                    setIsDeletePending(true)
                    dispatch(
                      displayModal({
                        show: true,
                        type: 'closeBanner',
                        confirmation: undefined,
                      })
                    )
                  }}
                >
                  Close
                </Box>
              )}
              {showDeleteOption && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    color: 'red',
                    '&:hover': { bg: 'lightgray' },
                  }}
                  onClick={() => {
                    setMenuOpen(false)
                    setIsDeletePending(true)
                    dispatch(
                      displayModal({
                        show: true,
                        type: 'deleteBanner',
                        confirmation: undefined,
                      })
                    )
                  }}
                >
                  Delete
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      <Flex justifyContent="space-between" alignItems="flex-start">
        {/* OrgName & Banner Type */}
        <Box sx={{ maxWidth: '60%' }}>
          <Heading as="h6" fontSize={3}>
            {orgName}
          </Heading>
          <Text fontSize={2} color="Red" mb={2}>
            {bannerType === 0
              ? 'Static Banner'
              : bannerType === 1
              ? 'Dismissible Banner'
              : 'Collapsible Banner'}
          </Text>
          <Text
            fontSize={2}
            color={
              bannerSeverity === 0
                ? 'primary'
                : bannerSeverity === 1
                ? 'red'
                : 'black'
            }
            mb={2}
          >
            {bannerSeverity === 0
              ? 'BannerSeverity: Information'
              : bannerSeverity === 1
              ? 'BannerSeverity: Incidental'
              : 'BannerSeverity: None'}
          </Text>

          <Text fontSize={1} color="gray" mt={1}>
            Created by: {createdBy}
          </Text>

          <Text fontSize={1} color="gray" mt={1}>
            Posted Orgs: {scopeDisplay}
          </Text>

          <Text fontSize={2} mt={4}>
            {message}
          </Text>
        </Box>

        {/* Banner Status */}
        <Box sx={{ textAlign: 'right', maxWidth: '40%' }}>
          <Box
            sx={{
              px: 3,
              py: 1,
              borderRadius: 9999,
              fontSize: 0,
              fontWeight: 'bold',
              color:
                bannerStatus === BannerStatus.Active ||
                bannerStatus === BannerStatus.Cancelled
                  ? 'white'
                  : 'black',
              bg:
                bannerStatus === BannerStatus.Active
                  ? 'green'
                  : bannerStatus === BannerStatus.Upcoming
                  ? 'lightgrey'
                  : bannerStatus === BannerStatus.Cancelled
                  ? 'red'
                  : 'lightgrey',
              display: 'inline-block',
              mb: 2,
            }}
          >
            {bannerStatus}
          </Box>

          <Flex flexDirection="column" alignItems="flex-end" pt={2}>
            <Text fontSize={1} color="gray">
              Start: {utcToLocalDisplay(startTime)}
            </Text>
            <Text fontSize={1} color="gray">
              End: {utcToLocalDisplay(endTime)}
            </Text>
          </Flex>

          {/* Approval List */}
          {approvalList && approvalList.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {approvalList.map((appr, index) => (
                <Flex
                  key={index}
                  justifyContent="flex-end" // Push badge to the right
                  mt={2}
                >
                  <Flex
                    alignItems="center"
                    sx={{
                      bg: appr.isApproved ? 'green' : 'red',
                      color: 'white',
                      borderRadius: 9999,
                      px: 3,
                      py: 1,
                      fontSize: 1,
                      fontWeight: 'bold',
                      gap: '8px',
                      width: 'fit-content',
                    }}
                  >
                    {/* Icon Circle */}
                    <Box
                      sx={{
                        width: '18px',
                        height: '18px',
                        minWidth: '18px',
                        minHeight: '18px',
                        aspectRatio: '1 / 1',
                        flexShrink: 0,
                        borderRadius: '50%',
                        bg: 'white',
                        color: appr.isApproved ? 'green' : 'red',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {appr.isApproved ? '✓' : '✕'}
                    </Box>

                    {/* Label */}
                    <Text
                      title={`${appr.userName} | ${formatApprovalTimestamp(
                        appr.timeStamp
                      )}`}
                      sx={{
                        display: 'block',
                        width: '100%',
                        fontSize: 1,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      {appr.hca34}
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </Box>
          )}
        </Box>
      </Flex>
    </Card>
  )
}

export default TileCard
